import classNames from "classnames";
import {
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import {
	clamp,
	ensureNotNil
} from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import { Anchor } from "../Anchor/Anchor";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import { Icon } from "../Icon/Icon";
import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	ReadonlyRefObject,
	RefObject,
	UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./Carousel.module.less";
import { IconButton } from "../IconButton/IconButton";

const AUTO_PLAY_INTERVAL_IN_MS = 5_000;

export type CarouselProps = {
	shouldLoopAround?: boolean;
	isNavigationBarHidden?: boolean;
	onScroll?: (scrollLeft: number) => void;
	isAutoPlayEnabled?: boolean;
};

export const Carousel = (props: PropsWithChildren<CarouselProps>): ReactElement => {
	const {
		shouldLoopAround = false,
		isAutoPlayEnabled = false,
		isNavigationBarHidden = false,
		onScroll,
		children
	} = props;

	const [scrollOffset, setScrollOffset] = useState<number>(0);
	const [didDisableAutoPlay, setDidDisableAutoPlay] = useState<boolean>(false);
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const [activeTouchIds, setActiveTouchIds] = useState<readonly number[]>([]);

	const stripRef: RefObject<HTMLOListElement | null> = useRef<HTMLOListElement>(null);

	const {
		width,
		wasInitialized
	} = useResizeObserver(stripRef);

	const childrenAsArray: ReactNode[] = flattenChildren(children);

	const amountOfItems: number = childrenAsArray.length;

	const currentItemIndex = useMemo(
		(): number => {
			if (!wasInitialized) {
				return 0;
			}

			return clamp(
				Math.floor(
					(scrollOffset / width) + 0.5
				),
				0,
				amountOfItems
			);
		},
		[
			amountOfItems,
			scrollOffset,
			wasInitialized,
			width
		]
	);

	const scrollToIndex = useCallback(
		(index: number): void => {
			ensureNotNil(stripRef.current)
				.scrollTo({
					behavior: "smooth",
					left: width * index
				});
		},
		[width]
	);

	const scrollToPreviousItemHandler = useMemo(
		(): (() => void) | undefined => {
			const canScrollToPreviousItem: boolean = shouldLoopAround
				? amountOfItems > 1
				: currentItemIndex > 0;

			return canScrollToPreviousItem
				? () => scrollToIndex((currentItemIndex - 1 + amountOfItems) % amountOfItems)
				: undefined;
		},
		[
			amountOfItems,
			currentItemIndex,
			scrollToIndex,
			shouldLoopAround
		]
	);

	const scrollToNextItemHandler = useMemo(
		(): (() => void) | undefined => {
			const canScrollToPreviousItem: boolean = shouldLoopAround
				? amountOfItems > 1
				: currentItemIndex < amountOfItems - 1;

			return canScrollToPreviousItem
				? () => scrollToIndex((currentItemIndex + 1) % amountOfItems)
				: undefined;
		},
		[
			amountOfItems,
			currentItemIndex,
			scrollToIndex,
			shouldLoopAround
		]
	);

	const scrollToNextItemHandlerRef: ReadonlyRefObject<(() => void) | undefined> = useSynchronizedRef(scrollToNextItemHandler);

	const navigateToIndexHandler = (args: MouseEvent, itemIndex: number): void => {
		args.preventDefault();
		scrollToIndex(itemIndex);
	};

	const scrollHandler = useCallback(
		(args: UIEvent<HTMLOListElement>): void => {
			const { scrollLeft } = args.currentTarget;
			setScrollOffset(scrollLeft);
			onScroll?.(scrollLeft);
		},
		[onScroll]
	);

	const hasAnyActiveTouches: boolean = activeTouchIds.length > 0;

	useEffect(
		(): (() => void) | void => {
			if (!isAutoPlayEnabled
				|| didDisableAutoPlay
				|| isHovered
				|| hasAnyActiveTouches
			) {
				return;
			}

			const handle: SetIntervalHandle = setInterval(
				(): void => {
					scrollToNextItemHandlerRef.current?.();
				},
				AUTO_PLAY_INTERVAL_IN_MS
			);

			return (): void => {
				clearInterval(handle);
			};
		},
		[
			isAutoPlayEnabled,
			didDisableAutoPlay,
			isHovered,
			scrollToNextItemHandlerRef,
			hasAnyActiveTouches
		]
	);

	const touchStartHandler = useCallback(
		(args: React.TouchEvent): void => {
			setActiveTouchIds(
				(x) => [
					...x,
					...Array.from(args.changedTouches)
						.map((x) => x.identifier)
				]
			);
		},
		[]
	);

	const touchEndHandler = useCallback(
		(args: React.TouchEvent): void => {
			const endedTouches: readonly number[] = Array.from(args.changedTouches)
				.map((x) => x.identifier);

			setActiveTouchIds(
				(x) => x.filter(
					(id) => !endedTouches.includes(id)
				)
			);
		},
		[]
	);

	return (
		<div
			className={styles.root}
			onPointerEnter={() => setIsHovered(true)}
			onPointerLeave={() => setIsHovered(false)}
			onTouchStart={touchStartHandler}
			onTouchEnd={touchEndHandler}
			onTouchCancel={touchEndHandler}
		>
			<ol
				className={styles.strip}
				onScroll={scrollHandler}
				ref={stripRef}
			>
				{childrenAsArray.map((x, i) => (
					<CarouselItem key={getReactNodeKey(x, i)}>
						{x}
					</CarouselItem>
				))}
			</ol>
			{!isNavigationBarHidden && (
				<ol className={styles.navigationBar}>
					{childrenAsArray.map((x, i) => (
						<li key={getReactNodeKey(x, i)}>
							<Anchor
								className={classNames(
									styles.navigationBullet,
									{ [styles.navigationBullet_active]: i === currentItemIndex }
								)}
								onClick={(args) => navigateToIndexHandler(args, i)}
							/>
						</li>
					))}
				</ol>
			)}

			{isAutoPlayEnabled && (
				<div className={styles.autoPlayButton}>
					<IconButton
						kind="transparent-colored"
						icon={{
							kind: "font",
							name: didDisableAutoPlay ? "circle-play" : "circle-pause",
							color: "white"
						}}
						size="large"
						trackingId={didDisableAutoPlay ? "enable-autoplay" : "disable-autoplay"}
						onClick={() => setDidDisableAutoPlay((x) => !x)}
					/>
				</div>
			)}

			{scrollToPreviousItemHandler && (
				<ButtonBase
					className={classNames(
						styles.navigationButton,
						styles.navigationButton_previous
					)}
					onClick={scrollToPreviousItemHandler}
				>
					<Icon value="chevron-left" size="large" />
				</ButtonBase>
			)}

			{scrollToNextItemHandler && (
				<ButtonBase
					className={classNames(
						styles.navigationButton,
						styles.navigationButton_next
					)}
					onClick={scrollToNextItemHandler}
				>
					<Icon value="chevron-right" size="large" />
				</ButtonBase>
			)}
		</div>
	);
};
Carousel.displayName = "Carousel";

const CarouselItem = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<li className={styles.item}>
			{children}
		</li>
	);
};
CarouselItem.displayName = "CarouselItem";
